import { environment } from '../../environment/environment';

type BuildAssets = {
  main: string | null;
  styles: string | null;
  scripts: string[];
  css: string[];
};

function safeGetDocument(): Document | null {
  try {
    return typeof document !== 'undefined' ? document : null;
  } catch {
    return null;
  }
}

function safeGetWindow(): Window | null {
  try {
    return typeof window !== 'undefined' ? window : null;
  } catch {
    return null;
  }
}

function getBuildAssets(): BuildAssets {
  const doc = safeGetDocument();
  if (!doc) {
    return { main: null, styles: null, scripts: [], css: [] };
  }

  const scripts = Array.from(doc.querySelectorAll('script[src]'))
    .map(s => (s as HTMLScriptElement).src)
    .filter(Boolean);

  const css = Array.from(doc.querySelectorAll('link[rel="stylesheet"][href]'))
    .map(l => (l as HTMLLinkElement).href)
    .filter(Boolean);

  const main =
    scripts.find(src => /\/main-[^/]+\.m?js(\?|$)/.test(src)) ??
    scripts.find(src => /\/main\.m?js(\?|$)/.test(src)) ??
    null;

  const styles =
    css.find(href => /\/styles-[^/]+\.css(\?|$)/.test(href)) ??
    css.find(href => /\/styles\.css(\?|$)/.test(href)) ??
    null;

  return { main, styles, scripts, css };
}

function extractAssetHash(url: string | null, prefix: string): string | null {
  if (!url) {
    return null;
  }

  const name = url.split('?')[0]?.split('/').pop() ?? '';
  const match = name.match(new RegExp(`^${prefix}-([A-Za-z0-9]+)\\.`));
  return match?.[1] ?? null;
}

function getResourceTiming(url: string | null): any | null {
  if (!url || typeof performance === 'undefined' || typeof performance.getEntriesByName !== 'function') {
    return null;
  }

  const entry = performance.getEntriesByName(url).at(-1) as PerformanceResourceTiming | undefined;
  if (!entry) {
    return null;
  }

  return {
    name: entry.name,
    initiatorType: entry.initiatorType,
    duration: Math.round(entry.duration),
    transferSize: entry.transferSize,
    encodedBodySize: entry.encodedBodySize,
    decodedBodySize: entry.decodedBodySize,
    startTime: Math.round(entry.startTime),
  };
}

export function logBuildInfo(): void {
  const w = safeGetWindow();
  const assets = getBuildAssets();
  const mainHash = extractAssetHash(assets.main, 'main');
  const stylesHash = extractAssetHash(assets.styles, 'styles');

  const telegram = (w as any)?.Telegram?.WebApp;
  const tgInfo =
    telegram
      ? {
          platform: telegram.platform,
          version: telegram.version,
          colorScheme: telegram.colorScheme,
          initDataLength: typeof telegram.initData === 'string' ? telegram.initData.length : null,
        }
      : null;

  const info = {
    location: w?.location?.href ?? null,
    env: {
      production: environment.production,
      API_URL: environment.API_URL,
      BASE_URL: environment.BASE_URL,
    },
    assets: {
      main: assets.main,
      styles: assets.styles,
      mainHash,
      stylesHash,
    },
    timing: {
      main: getResourceTiming(assets.main),
      styles: getResourceTiming(assets.styles),
    },
    telegram: tgInfo,
    now: new Date().toISOString(),
  };

  // Expose for easy copy/paste in DevTools.
  try {
    (w as any).__PG_BUILD_INFO__ = info;
  } catch {}

  // Keep logs compact: one collapsed group + one-line summary.
  console.info('[PG] build', { mainHash, stylesHash, production: environment.production });
  // eslint-disable-next-line no-console
  console.groupCollapsed?.('[PG] build info');
  // eslint-disable-next-line no-console
  console.log(info);
  // eslint-disable-next-line no-console
  console.groupEnd?.();
}

